import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Shield, Menu, X, ChevronDown, Zap, BookOpen, Users, ArrowRight, Sparkles, Layers, UserPlus, Bot, Activity } from 'lucide-react';

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
  }, [location]);

  const navItems = [
    {
      name: 'Platform',
      href: '/features',
      dropdown: [
        { name: 'Features', href: '/features', icon: Zap, description: 'Explore all platform capabilities' },
        { name: 'Architecture', href: '/architecture', icon: Layers, description: 'System design and flow' },
        { name: 'Governance Dashboard', href: '/governance', icon: Activity, description: 'Live Executive Council simulation' },
        { name: 'Executive Council', href: '/features#council', icon: Users, description: 'Distributed AI governance' },
        { name: 'Autonomy Tiers', href: '/features#tiers', icon: Shield, description: 'Five-tier control model' },
      ]
    },
    {
      name: 'Digital Employees',
      href: '/employees',
      dropdown: [
        { name: 'Employee Dashboard', href: '/employees', icon: Bot, description: 'Manage your AI workforce' },
        { name: 'Onboard New Employee', href: '/onboarding', icon: UserPlus, description: 'Provision a new digital employee' },
      ]
    },
    {
      name: 'Nellie Ross',
      href: '/about#nellie',
      dropdown: [
        { name: 'Meet Nellie', href: '/about#nellie', icon: Sparkles, description: 'Your Chief Agentic Officer' },
        { name: 'The CAO Oath', href: '/about#oath', icon: Shield, description: 'Core principles and values' },
      ]
    },
    { name: 'Docs', href: '/docs', icon: BookOpen },
    { name: 'About', href: '/about' },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-cao-dark/90 backdrop-blur-xl border-b border-blue-500/10 shadow-lg shadow-blue-500/5'
          : 'bg-transparent'
      }`}
    >
      {/* Animated top line */}
      <div className="absolute top-0 left-0 right-0 h-px">
        <div className="h-full bg-gradient-to-r from-transparent via-blue-500/50 to-transparent animate-pulse-slow" />
      </div>

      <div className="max-w-7xl mx-auto px-6">
        <nav className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <div className="relative">
              {/* Glow effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl blur-lg opacity-0 group-hover:opacity-50 transition-opacity duration-500" />

              {/* Logo container */}
              <div className="relative w-11 h-11 rounded-xl flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300 overflow-hidden">
                <img src="/CAO-Icon.svg" alt="CAO Platform" className="w-full h-full object-contain" />

                {/* Corner accents */}
                <div className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-cyan-400 rounded-full animate-pulse" />
              </div>
            </div>

            <div className="hidden sm:block">
              <span className="text-lg font-bold text-white group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-blue-400 group-hover:to-purple-400 transition-all duration-300">
                CAO Platform
              </span>
              <span className="block text-[10px] text-slate-500 -mt-0.5">
                Executive AI Governance
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => (
              <div
                key={item.name}
                className="relative"
                onMouseEnter={() => item.dropdown && setActiveDropdown(item.name)}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                <Link
                  to={item.href}
                  className={`flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                    location.pathname === item.href
                      ? 'text-blue-400 bg-blue-500/10'
                      : 'text-slate-300 hover:text-white hover:bg-white/5'
                  }`}
                >
                  {item.name}
                  {item.dropdown && (
                    <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${
                      activeDropdown === item.name ? 'rotate-180' : ''
                    }`} />
                  )}
                </Link>

                {/* Dropdown Menu */}
                {item.dropdown && activeDropdown === item.name && (
                  <div className="absolute top-full left-0 pt-2 w-72 animate-fade-in">
                    <div className="bg-cao-dark/95 backdrop-blur-xl rounded-xl border border-blue-500/20 shadow-xl shadow-blue-500/10 overflow-hidden">
                      {/* Scan line effect */}
                      <div className="absolute inset-0 overflow-hidden pointer-events-none">
                        <div className="absolute inset-x-0 h-20 bg-gradient-to-b from-blue-500/5 to-transparent animate-scan-line" />
                      </div>

                      <div className="relative p-2">
                        {item.dropdown.map((subItem, i) => (
                          <Link
                            key={i}
                            to={subItem.href}
                            className="flex items-start gap-3 p-3 rounded-lg hover:bg-blue-500/10 transition-colors group"
                          >
                            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500/20 to-purple-500/20 flex items-center justify-center group-hover:from-blue-500/30 group-hover:to-purple-500/30 transition-colors">
                              <subItem.icon className="w-5 h-5 text-blue-400" />
                            </div>
                            <div>
                              <span className="block text-sm font-medium text-white group-hover:text-blue-400 transition-colors">
                                {subItem.name}
                              </span>
                              <span className="text-xs text-slate-500">
                                {subItem.description}
                              </span>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-3">
            <Link
              to="/demo"
              className="px-4 py-2 text-sm font-medium text-slate-300 hover:text-white transition-colors"
            >
              Watch Demo
            </Link>
            <Link
              to="/contact"
              className="group relative px-5 py-2.5 rounded-lg font-semibold text-sm overflow-hidden"
            >
              {/* Button background with animation */}
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 transition-transform duration-300 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-purple-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

              {/* Shine effect */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <div className="absolute inset-0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700 bg-gradient-to-r from-transparent via-white/20 to-transparent" />
              </div>

              <span className="relative flex items-center gap-2 text-white">
                Get Started
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </span>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center rounded-lg bg-slate-800/50 border border-slate-700/50 text-slate-300 hover:text-white hover:border-blue-500/50 transition-all"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <X className="w-5 h-5" />
            ) : (
              <Menu className="w-5 h-5" />
            )}
          </button>
        </nav>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden absolute top-full left-0 right-0 bg-cao-dark/95 backdrop-blur-xl border-b border-blue-500/10 animate-slide-down">
            <div className="p-6 space-y-4">
              {navItems.map((item) => (
                <div key={item.name}>
                  <Link
                    to={item.href}
                    className={`flex items-center justify-between py-3 px-4 rounded-lg transition-colors ${
                      location.pathname === item.href
                        ? 'text-blue-400 bg-blue-500/10'
                        : 'text-slate-300 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    <span className="font-medium">{item.name}</span>
                    {item.dropdown && <ChevronDown className="w-4 h-4" />}
                  </Link>

                  {item.dropdown && (
                    <div className="mt-2 ml-4 space-y-2">
                      {item.dropdown.map((subItem, i) => (
                        <Link
                          key={i}
                          to={subItem.href}
                          className="flex items-center gap-3 py-2 px-4 text-sm text-slate-400 hover:text-white transition-colors"
                        >
                          <subItem.icon className="w-4 h-4 text-blue-400" />
                          {subItem.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              <div className="pt-4 border-t border-slate-800 space-y-3">
                <Link
                  to="/demo"
                  className="block w-full py-3 px-4 text-center text-slate-300 rounded-lg border border-slate-700 hover:border-blue-500/50 hover:text-white transition-all"
                >
                  Watch Demo
                </Link>
                <Link
                  to="/contact"
                  className="block w-full py-3 px-4 text-center text-white font-semibold rounded-lg bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 transition-all"
                >
                  Get Started
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
